import { z } from 'zod'

import { ensureRouteData } from '@/app/query-client'
import { sectorsOverviewQueryOptions } from '@/features/sectors/api/queries'
import { sectorViewSchema } from '@/schemas/sectors'
import type { RouterContext } from '@/routes/layouts/root'

/**
 * `?view=` here rather than in SectorsScreen.tsx because the loader below
 * parses the URL through it too, and the route's `validateSearch` and the
 * loader must not be able to disagree about which view a URL means.
 */
export const sectorsSearchSchema = z.object({ view: sectorViewSchema.default('turnover') })

/**
 * The `/market/sectors` loader.
 *
 * MAIN: the whole tab — the strip, the donut and the seven cards are one
 * query keyed on `view` — awaited for the URL's own view. No secondary reads:
 * nothing else on this screen is fetched.
 *
 * `?view=` is read from `location.search` through the route's own schema, not
 * through `loaderDeps`: a dep would make every chip click a new route match —
 * the full-page pending skeleton on each one. As it is, a chip click runs no
 * loader at all; `SectorsScreen` re-keys `useSectors` for itself and dims the
 * previous view's cards while the new one loads (`keepPreviousData`).
 *
 * Its own module, as `app.guard.ts` is, so a test can mount it under a root
 * of its own: typed inside `createFileRoute`, a loader is bound to this
 * route's parent in the generated tree.
 */
export async function loadSectors({
  context: { queryClient },
  location,
}: {
  context: RouterContext
  location: { search: unknown }
}): Promise<void> {
  const { view } = sectorsSearchSchema.parse(location.search)
  await ensureRouteData(queryClient, sectorsOverviewQueryOptions(view))
}
